import {
  CreateSettingRequestConfig,
  DeleteSettingRequestConfig,
  SettingsRequestConfig,
  UpdateSettingRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import { createEffect } from "solid-js";
import { isServer } from "solid-js/web";
import { USER_SETTINGS_MUTATION_SCOPE, USER_SETTINGS_QUERY_KEY } from "~/constants/userSettings";
import { useTheme } from "~/hooks/useTheme";
import type { ThemePreference } from "~/utils/themeCookie";
import { useWire } from "~/wire";
import { authTokenReady } from "~/wire/authTokenReady";

const KEY_THEME = "ui.theme";

// Once per page load rather than per consumer: a consumer that remounts on
// navigation would otherwise re-seed from a stale cache and undo a click the
// user just made.
let seeded = false;

/**
 * The theme choice, mirrored into the per-user settings store so it follows the
 * user to another browser. The cookie stays what paints the page — a fetch can
 * only resolve after the first paint — so this reconciles rather than replaces
 * it. Read-only consumers want useTheme instead; this one issues a request.
 */
export function useThemePreference() {
  const wire = useWire();
  const client = useStorytellClient();
  const queryClient = useQueryClient();
  const { themePreference, setThemePreference } = useTheme();

  const fetchSettings = async (): Promise<Record<string, string>> => {
    const res = await client.fetch(SettingsRequestConfig.withBearerToken(wire.auth.token()), undefined, undefined);
    if (res.isErr()) throw res.error;
    return res.value.settings;
  };

  const query = useQuery(() => ({
    queryKey: USER_SETTINGS_QUERY_KEY,
    enabled: !isServer && authTokenReady(wire.auth),
    staleTime: 5 * 60_000,
    queryFn: fetchSettings,
  }));

  const mutation = useMutation(() => ({
    scope: USER_SETTINGS_MUTATION_SCOPE,
    mutationFn: async (pref: ThemePreference): Promise<void> => {
      // Read live so create-vs-update matches the keys that exist now: the store
      // has no upsert, the cached map can label an existing key new, and
      // create-on-existing is rejected server-side.
      const existing = await queryClient.fetchQuery({
        queryKey: USER_SETTINGS_QUERY_KEY,
        queryFn: fetchSettings,
        staleTime: 0,
      });
      const token = wire.auth.token();

      // Following the OS is stored as the absence of a row, so it deletes.
      const res =
        pref === "system"
          ? await client.fetch(DeleteSettingRequestConfig.withBearerToken(token), undefined, { key: KEY_THEME })
          : await client.fetch(
              (KEY_THEME in existing ? UpdateSettingRequestConfig : CreateSettingRequestConfig).withBearerToken(token),
              undefined,
              { key: KEY_THEME, value: pref }
            );
      if (res.isErr()) throw res.error;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: USER_SETTINGS_QUERY_KEY }),
  }));

  // Adopt the stored choice on the first resolve, then leave local authoritative
  // so a later refetch can't revert a click. This is also what corrects the
  // cookie when a different user signs in on a shared browser.
  createEffect(() => {
    const settings = query.data;
    if (!settings || seeded) return;
    seeded = true;

    const stored = settings[KEY_THEME];
    setThemePreference(stored === "light" || stored === "dark" ? stored : "system");
  });

  return {
    themePreference,
    setThemePreference: (pref: ThemePreference) => {
      // A click before the query first resolves is still an explicit choice, so it
      // closes the seeding window rather than waiting to be overwritten by it.
      seeded = true;
      setThemePreference(pref);
      if (authTokenReady(wire.auth)) mutation.mutate(pref);
    },
  };
}
